(() => {
  const storageKey = 'gs-recently-viewed-products';
  const maximumProducts = 12;
  const productDetailsCache = new Map();
  const productReviewBadgeCache = new Map();

  function readRecentlyViewedProducts() {
    try {
      const storedProducts = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(storedProducts) ? storedProducts : [];
    } catch (error) {
      console.warn('Unable to read recently viewed products.', error);
      return [];
    }
  }

  function writeRecentlyViewedProducts(products) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(products.slice(0, maximumProducts)));
    } catch (error) {
      console.warn('Unable to save recently viewed products.', error);
    }
  }

  function normalizeProduct(product) {
    if (!product || !product.handle) return null;

    const featuredImage = typeof product.featured_image === 'string'
      ? product.featured_image
      : product.featured_image?.src || product.image;

    const variants = Array.isArray(product.variants)
      ? product.variants.map((variant) => ({
        id: variant.id,
        available: variant.available !== false,
      }))
      : [];
    const price = Number(product.price ?? product.price_min ?? 0);
    const priceMin = Number(product.price_min ?? product.priceMin ?? price);
    const priceMax = Number(product.price_max ?? product.priceMax ?? price);

    return {
      id: product.id,
      handle: product.handle,
      title: product.title || product.handle,
      url: product.url || `/products/${encodeURIComponent(product.handle)}`,
      image: featuredImage || product.images?.[0] || '',
      price,
      priceMin,
      priceMax,
      priceVaries: product.price_varies === true || product.priceVaries === true || priceMin !== priceMax,
      compareAtPrice: Number(product.compare_at_price ?? product.compare_at_price_min ?? product.compareAtPrice ?? 0),
      variants,
      available: product.available !== false,
    };
  }

  async function recordCurrentProduct() {
    const productDetails = document.querySelector('[data-product-handle][data-product-json-url]');
    if (!productDetails) return null;

    try {
      const response = await fetch(productDetails.dataset.productJsonUrl, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Product request failed with status ${response.status}.`);

      const product = normalizeProduct(await response.json());
      if (!product) return null;

      const previousProducts = readRecentlyViewedProducts().filter(
        (previousProduct) => previousProduct.handle !== product.handle
      );
      writeRecentlyViewedProducts([product, ...previousProducts]);
      return product;
    } catch (error) {
      console.warn('Unable to record the current product as recently viewed.', error);
      return null;
    }
  }

  function formatPrice(price) {
    const currencyCode = window.Shopify?.currency?.active || 'GBP';
    const amount = Number(price || 0) / 100;

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch (error) {
      return `${currencyCode} ${amount.toFixed(2)}`;
    }
  }

  function getProductUrl(product) {
    return product.url || `/products/${encodeURIComponent(product.handle)}`;
  }

  function getProductJsonUrl(product) {
    const productUrl = getProductUrl(product).replace(/\/$/, '');
    return productUrl.endsWith('.js') ? productUrl : `${productUrl}.js`;
  }

  function getPriceLabel(product, section) {
    const price = formatPrice(product.priceVaries ? product.priceMin : product.price);
    const fromLabel = section.dataset.priceFromLabel || 'From [price]';
    return product.priceVaries ? fromLabel.replace('[price]', price) : price;
  }

  function createLoadingSpinner() {
    const spinner = document.createElement('span');
    spinner.className = 'loading__spinner hidden';
    spinner.innerHTML =
      '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
      '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
      '</svg>';
    return spinner;
  }

  function createPriceMarkup(product, section) {
    const priceWrapper = document.createElement('div');
    priceWrapper.className = 'complementary-product-card__price';

    const price = document.createElement('div');
    const isOnSale = product.compareAtPrice > product.price;
    price.className = `price${isOnSale ? ' price--on-sale' : ''}`;

    const priceContainer = document.createElement('div');
    priceContainer.className = 'price__container';

    const regular = document.createElement('div');
    regular.className = 'price__regular';
    const regularPrice = document.createElement('span');
    regularPrice.className = 'price-item price-item--regular';
    regularPrice.textContent = getPriceLabel(product, section);
    regular.appendChild(regularPrice);

    const sale = document.createElement('div');
    sale.className = 'price__sale';

    if (isOnSale) {
      const comparePrice = document.createElement('span');
      comparePrice.className = 'price__compare-price';
      const comparePriceValue = document.createElement('s');
      comparePriceValue.className = 'price-item price-item--regular';
      comparePriceValue.textContent = formatPrice(product.compareAtPrice);
      comparePrice.appendChild(comparePriceValue);

      const salePrice = document.createElement('span');
      salePrice.className = 'price-item price-item--sale price-item--last';
      salePrice.textContent = getPriceLabel(product, section);
      sale.append(comparePrice, salePrice);
    }

    priceContainer.append(regular, sale);
    price.appendChild(priceContainer);
    priceWrapper.appendChild(price);
    return priceWrapper;
  }

  function createReviewBadge(product, reviewBadgeHtml) {
    const badge = document.createElement('div');
    badge.className = 'jdgm-widget jdgm-preview-badge complementary-product-card__rating';
    badge.dataset.id = String(product.id || '');
    badge.dataset.autoInstall = 'false';
    if (reviewBadgeHtml) badge.innerHTML = reviewBadgeHtml;
    return badge;
  }

  function createReviewOverrideSource(reviewOverride) {
    if (!reviewOverride) return null;

    const source = document.createElement('span');
    source.className = 'gs-review-count-override';
    source.hidden = true;
    Object.entries(reviewOverride).forEach(([key, value]) => {
      source.dataset[key] = String(value);
    });
    return source;
  }

  function createQuickAddModal(product, section, productFormId, modalId) {
    const template = document.createElement('template');
    template.innerHTML =
      `<quick-add-modal id="${modalId}" class="quick-add-modal">` +
      `<div role="dialog" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">` +
      `<button id="ModalClose-${productFormId}" type="button" class="quick-add-modal__toggle"></button>` +
      `<div id="QuickAddInfo-${productFormId}" class="quick-add-modal__content-info"></div>` +
      '</div>' +
      '</quick-add-modal>';

    const modal = template.content.firstElementChild;
    const dialog = modal.querySelector('[role="dialog"]');
    dialog.setAttribute(
      'aria-label',
      (section.dataset.chooseProductOptionsLabel || 'Choose options for [product_name]')
        .replace('[product_name]', product.title)
    );

    const closeButton = modal.querySelector('.quick-add-modal__toggle');
    closeButton.setAttribute('aria-label', section.dataset.closeLabel || 'Close');
    closeButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17">' +
      '<path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path>' +
      '</svg>';
    return modal;
  }

  function createProductAction(product, section) {
    const action = document.createElement('div');
    action.className = 'quick-add no-js-hidden complementary-product-card__action';

    const productKey = `${section.dataset.recommendationSectionId}-${product.id || product.handle}`;
    const productFormId = `recently-viewed-${productKey}`;
    const titleId = `RecentlyViewedTitle-${productKey}`;
    const productUrl = getProductUrl(product);
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const hasMultipleVariants = variants.length > 1;

    if (hasMultipleVariants) {
      const modalId = `QuickAdd-${productFormId}`;
      const openerTemplate = document.createElement('template');
      openerTemplate.innerHTML = '<modal-opener></modal-opener>';
      const opener = openerTemplate.content.firstElementChild;
      opener.dataset.modal = `#${modalId}`;

      const button = document.createElement('button');
      button.id = `${productFormId}-submit`;
      button.type = 'button';
      button.className = 'quick-add__submit button button--full-width button--secondary animate-arrow';
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-labelledby', `${productFormId}-submit ${titleId}`);
      button.dataset.productUrl = productUrl;
      button.textContent = section.dataset.chooseOptionsLabel || 'Choose options';
      button.appendChild(createLoadingSpinner());

      opener.appendChild(button);
      action.appendChild(opener);
      action.appendChild(createQuickAddModal(product, section, productFormId, modalId));
      return action;
    }

    const variant = variants.find((candidate) => candidate.available) || variants[0];
    if (!variant?.id) {
      const link = document.createElement('a');
      link.className = 'quick-add__submit button button--full-width button--secondary';
      link.href = productUrl;
      link.textContent = section.dataset.viewProductLabel || 'View full details';
      action.appendChild(link);
      return action;
    }

    const form = document.createElement('form');
    form.className = 'form';
    form.method = 'post';
    form.action = window.routes?.cart_add_url || '/cart/add';
    form.noValidate = true;
    form.dataset.type = 'add-to-cart-form';

    const variantInput = document.createElement('input');
    variantInput.type = 'hidden';
    variantInput.name = 'id';
    variantInput.value = String(variant.id);
    variantInput.className = 'product-variant-id';
    if (!variant.available) variantInput.disabled = true;

    const button = document.createElement('button');
    button.id = `${productFormId}-submit`;
    button.type = 'submit';
    button.name = 'add';
    button.className = 'quick-add__submit button button--full-width button--secondary';
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-labelledby', `${productFormId}-submit ${titleId}`);
    button.setAttribute('aria-live', 'polite');
    button.dataset.soldOutMessage = 'true';
    if (!variant.available) button.disabled = true;

    const label = document.createElement('span');
    label.textContent = variant.available
      ? section.dataset.addToCartLabel || 'Add to cart'
      : section.dataset.soldOutLabel || 'Sold out';
    const soldOutMessage = document.createElement('span');
    soldOutMessage.className = 'sold-out-message hidden';
    soldOutMessage.textContent = section.dataset.soldOutLabel || 'Sold out';
    button.append(label, soldOutMessage, createLoadingSpinner());

    form.append(variantInput, button);
    const productFormTemplate = document.createElement('template');
    productFormTemplate.innerHTML = '<product-form></product-form>';
    const productForm = productFormTemplate.content.firstElementChild;
    productForm.dataset.sectionId = section.dataset.recommendationSectionId;
    productForm.appendChild(form);
    action.appendChild(productForm);
    return action;
  }

  function createRecentlyViewedCard(product, section, reviewBadgeHtml, reviewOverride) {
    const item = document.createElement('li');
    item.className = 'complementary-product-list__item';

    const article = document.createElement('article');
    article.className = 'complementary-product-card';

    const header = document.createElement('div');
    header.className = 'complementary-product-card__header';

    const title = document.createElement('h3');
    title.className = 'complementary-product-card__title';
    title.id = `RecentlyViewedTitle-${section.dataset.recommendationSectionId}-${product.id || product.handle}`;
    const titleLink = document.createElement('a');
    titleLink.className = 'full-unstyled-link';
    titleLink.href = getProductUrl(product);
    titleLink.textContent = product.title;
    title.appendChild(titleLink);
    const reviewOverrideSource = createReviewOverrideSource(reviewOverride);
    header.append(title);
    if (reviewOverrideSource) header.appendChild(reviewOverrideSource);
    header.appendChild(createReviewBadge(product, reviewBadgeHtml));

    const mediaLink = document.createElement('a');
    mediaLink.className = 'complementary-product-card__media';
    mediaLink.href = getProductUrl(product);
    mediaLink.setAttribute('aria-label', product.title);

    if (product.image) {
      const image = document.createElement('img');
      image.className = 'complementary-product-card__image';
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      mediaLink.appendChild(image);
    }

    article.append(header, mediaLink, createPriceMarkup(product, section), createProductAction(product, section));
    item.appendChild(article);
    return item;
  }

  function updateRecentlyViewedIndicator(list, indicator) {
    if (!indicator) return;

    const recommendationList = list.closest('complementary-products-list');
    if (recommendationList?.updateScrollIndicator) {
      recommendationList.updateScrollIndicator();
      return;
    }

    const thumb = indicator.querySelector('.complementary-product-list__scroll-thumb');
    const scrollableWidth = list.scrollWidth - list.clientWidth;
    const thumbWidth = list.scrollWidth ? Math.max(30, (list.clientWidth / list.scrollWidth) * 100) : 30;
    const scrollProgress = scrollableWidth > 0 ? list.scrollLeft / scrollableWidth : 0;

    indicator.hidden = list.children.length <= 2;
    if (thumb) {
      thumb.style.width = `${thumbWidth}%`;
      thumb.style.transform = `translateX(${scrollProgress * (100 - thumbWidth)}%)`;
    }
  }

  async function loadRecentlyViewedProduct(product) {
    const handle = product.handle;
    let productDetails = normalizeProduct(product) || product;
    let reviewBadgeHtml = '';
    let reviewOverride = null;

    if (!productDetailsCache.has(handle)) {
      productDetailsCache.set(handle, (async () => {
        try {
          const response = await fetch(getProductJsonUrl(product), {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`Product details request failed with status ${response.status}.`);

          return normalizeProduct(await response.json()) || product;
        } catch (error) {
          console.warn(`Unable to refresh Recently Viewed product ${handle}.`, error);
          return normalizeProduct(product) || product;
        }
      })());
    }

    productDetails = await productDetailsCache.get(handle);

    if (!productReviewBadgeCache.has(handle)) {
      productReviewBadgeCache.set(handle, (async () => {
        try {
          const response = await fetch(getProductUrl(productDetails), {
            headers: { Accept: 'text/html' },
          });
          if (!response.ok) throw new Error(`Product page request failed with status ${response.status}.`);

          const html = await response.text();
          const productDocument = new DOMParser().parseFromString(html, 'text/html');
          const reviewOverrideElement = productDocument.querySelector('.gs-review-count-override');
          return {
            html: productDocument.querySelector('.jdgm-widget.jdgm-preview-badge')?.innerHTML || '',
            override: reviewOverrideElement
              ? {
                  productId: reviewOverrideElement.dataset.productId,
                  oneStarCount: reviewOverrideElement.dataset.oneStarCount,
                  twoStarCount: reviewOverrideElement.dataset.twoStarCount,
                  threeStarCount: reviewOverrideElement.dataset.threeStarCount,
                  fourStarCount: reviewOverrideElement.dataset.fourStarCount,
                  fiveStarCount: reviewOverrideElement.dataset.fiveStarCount,
                  totalCount: reviewOverrideElement.dataset.totalCount,
                  averageRating: reviewOverrideElement.dataset.averageRating,
                }
              : null,
          };
        } catch (error) {
          console.warn(`Unable to load reviews for Recently Viewed product ${handle}.`, error);
          return { html: '', override: null };
        }
      })());
    }

    const reviewData = await productReviewBadgeCache.get(handle);
    reviewBadgeHtml = reviewData.html;
    reviewOverride = reviewData.override;
    return { product: productDetails, reviewBadgeHtml, reviewOverride };
  }

  async function renderRecentlyViewedProducts(section) {
    const list = section.querySelector('[data-recently-viewed-list]');
    const emptyMessage = section.querySelector('[data-recently-viewed-empty]');
    const indicator = section.querySelector('[data-recently-viewed-indicator]');
    if (!list || !emptyMessage) return;

    const renderToken = `${Date.now()}-${Math.random()}`;
    section.dataset.recommendationRenderToken = renderToken;

    const currentHandle = document.querySelector('[data-product-handle]')?.dataset.productHandle;
    const recentlyViewedProducts = readRecentlyViewedProducts().filter(
      (product) => product.handle && product.handle !== currentHandle
    );

    if (!recentlyViewedProducts.length) {
      list.replaceChildren();
      emptyMessage.hidden = false;
      updateRecentlyViewedIndicator(list, indicator);
      return;
    }

    const productsWithDetails = await Promise.all(
      recentlyViewedProducts.map((product) => loadRecentlyViewedProduct(product))
    );

    if (section.dataset.recommendationRenderToken !== renderToken) return;

    list.replaceChildren(
      ...productsWithDetails.map(({ product, reviewBadgeHtml, reviewOverride }) =>
        createRecentlyViewedCard(product, section, reviewBadgeHtml, reviewOverride)
      )
    );
    emptyMessage.hidden = true;
    updateRecentlyViewedIndicator(list, indicator);
  }

  function activateTab(section, selectedTab) {
    const tabs = Array.from(section.querySelectorAll('[data-recommendation-tab]'));
    const panels = Array.from(section.querySelectorAll('[data-recommendation-panel]'));

    tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.recommendationPanel !== selectedTab.dataset.recommendationTab;
    });

    if (selectedTab.dataset.recommendationTab === 'recently-viewed') {
      renderRecentlyViewedProducts(section);
    }
  }

  function setupRecommendationTabs() {
    document.querySelectorAll('.everyone-buying[data-recommendation-tabs]').forEach((section) => {
      if (section.dataset.recommendationTabsReady === 'true') return;
      section.dataset.recommendationTabsReady = 'true';

      const tabs = Array.from(section.querySelectorAll('[data-recommendation-tab]'));
      if (!tabs.length) return;

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activateTab(section, tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;

          tabs[nextIndex].focus();
          activateTab(section, tabs[nextIndex]);
        });
      });

      const recentList = section.querySelector('[data-recently-viewed-list]');
      const recentIndicator = section.querySelector('[data-recently-viewed-indicator]');
      recentList?.addEventListener('scroll', () => updateRecentlyViewedIndicator(recentList, recentIndicator));
      renderRecentlyViewedProducts(section);
    });
  }

  window.GymSetsRecentlyViewed = {
    read: readRecentlyViewedProducts,
    recordCurrentProduct,
    setup: setupRecommendationTabs,
  };

  async function initialize() {
    setupRecommendationTabs();
    await recordCurrentProduct();
    document.querySelectorAll('.everyone-buying[data-recommendation-tabs]').forEach((section) => {
      renderRecentlyViewedProducts(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize().catch((error) => console.error('Unable to initialize Recently Viewed products.', error));
    }, { once: true });
  } else {
    initialize().catch((error) => console.error('Unable to initialize Recently Viewed products.', error));
  }

  document.addEventListener('shopify:section:load', setupRecommendationTabs);
})();
